"use client";

import { useEffect, useState } from "react";
import { APP_VERSION_SEMVER } from "@/lib/constants/version";
import { reportWarning } from "@/lib/observability/report-error";
import { fetchWithTransientRetry } from "@/lib/utils/fetch-with-transient-retry";

/**
 * Public display version from the latest changelog entry.
 * Falls back to web build semver (without git SHA) when unavailable.
 * The full CI version (with SHA) is only used internally by Sentry/Amplitude.
 */
export function useDesktopDisplayVersion(): string {
  const [version, setVersion] = useState<string | null>(null);
  useEffect(() => {
    const url = "/api/changelog/?page=1&limit=1&lang=en";
    (async () => {
      try {
        // SENTRY FIX: VIDSEEDS-2C6 adjacent — /api/changelog/ 503 during
        // rolling deploys. Same class of transient infra error handled in
        // featured-creators / creator-results. Consolidate on the shared
        // retry helper that also handles 5xx (the in-file loop only retried
        // 429).
        // Date: 2026-04-23 | Agent: Claude Code
        const res = await fetchWithTransientRetry(url, {
          attempts: 4,
          baseDelayMs: 600,
        });
        if (!res.ok) {
          return;
        }
        const data = (await res.json()) as {
          entries?: Array<{ version?: string }>;
        };
        const latest = data?.entries?.[0]?.version;
        if (latest) {
          setVersion(latest);
        }
      } catch (err) {
        reportWarning(err, {
          source: "hooks.useDesktopDisplayVersion",
          feature: "desktop",
          action: "fetchChangelog",
        });
      }
    })();
  }, []);
  return version ?? APP_VERSION_SEMVER;
}
